"use client";

import { useMemo } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useDebounceValue } from "usehooks-ts";
import { useUniversalSearch } from "@/lib/api/generated";
import type {
  UniversalSearchParams,
  UniversalSearchResponseSchema,
} from "@/lib/api/model";

export const UNIVERSAL_SEARCH_MIN_CHARS = 1;
const DEBOUNCE_MS = 200;
const RESULTS_PER_GROUP = 5;
/** "See more" expands one group to this many results. */
export const EXPANDED_RESULTS_PER_GROUP = 25;

export type UniversalSearchData = UniversalSearchResponseSchema;
export type UniversalSearchGroupKey = NonNullable<
  UniversalSearchParams["types"]
>[number];

export const UNIVERSAL_SEARCH_GROUP_KEYS = [
  "projects",
  "jobs",
  "candidates",
  "sequences",
  "searches",
  "tasks",
  "snippets",
  "presets",
] as const satisfies readonly UniversalSearchGroupKey[];

/** Copy one group from `source` onto `target`, keyed generically so the
 *  per-group item types stay correlated. */
function overlayGroup<K extends UniversalSearchGroupKey>(
  target: UniversalSearchResponseSchema,
  source: UniversalSearchResponseSchema,
  key: K,
): void {
  target[key] = source[key];
}

/**
 * Debounced Cmd+K entity search. Only fires while the palette is open and
 * the (trimmed) query has at least {@link UNIVERSAL_SEARCH_MIN_CHARS} chars.
 * Previous results are kept while the next request is in flight so the list
 * never flashes empty between keystrokes.
 *
 * `expandedTypes` drives "See more": expanded groups are re-queried in a
 * single follow-up request with a larger per-group limit and overlaid onto
 * the base response.
 */
export function useUniversalSearchResults(
  query: string,
  enabled: boolean,
  expandedTypes: readonly UniversalSearchGroupKey[] = [],
) {
  const trimmed = query.trim();
  const [debounced] = useDebounceValue(trimmed, DEBOUNCE_MS);
  const qualifies = trimmed.length >= UNIVERSAL_SEARCH_MIN_CHARS;
  // Gate on the *live* value too, so clearing the input drops results
  // immediately instead of after the debounce window.
  const effective = qualifies ? debounced : "";
  const active =
    enabled && effective.length >= UNIVERSAL_SEARCH_MIN_CHARS;
  // True while the typed text has moved past what the debounced query ran
  // with — results for the old text are stale and must not render.
  const debouncePending = enabled && qualifies && trimmed !== debounced;

  const base = useUniversalSearch(
    { q: effective, limit: RESULTS_PER_GROUP },
    {
      query: {
        enabled: active,
        staleTime: 30_000,
        retry: false,
      },
    },
  );

  const expanded = useUniversalSearch(
    {
      q: effective,
      limit: EXPANDED_RESULTS_PER_GROUP,
      types: [...expandedTypes],
    },
    {
      query: {
        enabled: active && expandedTypes.length > 0,
        staleTime: 30_000,
        placeholderData: keepPreviousData,
        retry: false,
      },
    },
  );

  const data = useMemo(() => {
    // While the debounce is pending the current results belong to the old
    // text — hide them so the palette drops into the skeleton state on every
    // query change, not just the first search.
    if (!active || debouncePending || !base.data) return undefined;
    if (!expandedTypes.length || !expanded.data) return base.data;
    const merged = { ...base.data };
    for (const key of expandedTypes) {
      overlayGroup(merged, expanded.data, key);
    }
    return merged;
  }, [active, base.data, debouncePending, expanded.data, expandedTypes]);

  const hasAnyResults =
    !!data &&
    UNIVERSAL_SEARCH_GROUP_KEYS.some((key) => data[key].items.length > 0);

  return {
    data,
    hasAnyResults,
    isSearching:
      debouncePending || (active && base.isFetching && !base.data),
    isExpanding: active && expandedTypes.length > 0 && expanded.isFetching,
    active,
  };
}
